'use client';

import { cn } from '@/lib/utils';
import {
  createContext,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { ActiveImageData, TabSectionContext, TabSectionContextType } from './tabSectionExperience';

export type TabGroupContextType = {
  isGroupActive: boolean;
  activeItemId: string | null;
  activateItem: (id: string, imageData?: ActiveImageData) => void;
  registerItem: (id: string, imageData?: ActiveImageData) => void;
  unregisterItem: (id: string) => void;
  updateItemImage: (id: string, imageData?: ActiveImageData) => void;
};

export const TabGroupContext = createContext<TabGroupContextType | null>(null);

function normalizeUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith('//') ? `https:${url}` : url;
}

export type TabGroupExperienceProps = {
  children?: ReactNode;
  title?: string;
  icon?: string | null;
};

export const TabGroupExperience = ({
  children,
  title = '',
  icon,
  className,
  ...props
}: TabGroupExperienceProps & HTMLAttributes<HTMLDivElement>) => {
  const sectionContext = useContext(TabSectionContext);
  const id = useId();

  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const activeItemIdRef = useRef<string | null>(null);
  activeItemIdRef.current = activeItemId;

  // Tracks mounted items: id → image data. Insertion order = visual order.
  const mountedItemsRef = useRef<Map<string, ActiveImageData | undefined>>(new Map());

  // Stable refs so callbacks don't need to re-create on every render.
  const sectionContextRef = useRef<TabSectionContextType | null>(null);
  sectionContextRef.current = sectionContext;

  const iconUrl = normalizeUrl(icon);
  const isGroupActive = sectionContext?.activeGroupId === id;
  const isGroupActiveRef = useRef(false);
  isGroupActiveRef.current = isGroupActive;

  // Register this group on mount; unregister on unmount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    sectionContext?.registerGroup(id, title, iconUrl);
    return () => sectionContext?.unregisterGroup(id);
  }, []);

  // Update group metadata when title or icon change (Studio live edits).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    sectionContext?.updateGroupMeta(id, title, iconUrl);
  }, [title, iconUrl]);

  // When this group becomes active, push the current item image to the section.
  useEffect(() => {
    if (isGroupActive) {
      const activeItemImageData = activeItemIdRef.current
        ? mountedItemsRef.current.get(activeItemIdRef.current)
        : undefined;
      sectionContextRef.current?.updateActiveImage(activeItemImageData ?? { url: undefined });
    }
  }, [isGroupActive]);

  const activateItem = useCallback((itemId: string, imageData?: ActiveImageData) => {
    if (activeItemIdRef.current === itemId) return;
    setActiveItemId(itemId);
    if (isGroupActiveRef.current) {
      sectionContextRef.current?.updateActiveImage(imageData ?? { url: undefined });
    }
  }, []);

  // Called on item mount. First item to mount becomes default active.
  const registerItem = useCallback((itemId: string, imageData?: ActiveImageData) => {
    mountedItemsRef.current.set(itemId, imageData);
    if (mountedItemsRef.current.size === 1) {
      setActiveItemId(itemId);
      if (isGroupActiveRef.current) {
        sectionContextRef.current?.updateActiveImage(imageData ?? { url: undefined });
      }
    }
  }, []);

  // Called on item unmount. If active item leaves, promote the first remaining.
  const unregisterItem = useCallback((itemId: string) => {
    mountedItemsRef.current.delete(itemId);
    if (activeItemIdRef.current === itemId) {
      const firstId = mountedItemsRef.current.keys().next().value as string | undefined;
      setActiveItemId(firstId ?? null);
      if (isGroupActiveRef.current) {
        const firstData = firstId ? mountedItemsRef.current.get(firstId) : undefined;
        sectionContextRef.current?.updateActiveImage(firstData ?? { url: undefined });
      }
    }
  }, []);

  // Called when an item's image prop changes — updates section if item is active.
  const updateItemImage = useCallback((itemId: string, imageData?: ActiveImageData) => {
    if (mountedItemsRef.current.has(itemId)) {
      mountedItemsRef.current.set(itemId, imageData);
    }
    if (activeItemIdRef.current === itemId && isGroupActiveRef.current) {
      sectionContextRef.current?.updateActiveImage(imageData ?? { url: undefined });
    }
  }, []);

  return (
    <TabGroupContext.Provider
      value={{
        isGroupActive,
        activeItemId,
        activateItem,
        registerItem,
        unregisterItem,
        updateItemImage,
      }}
    >
      <div className={cn(!isGroupActive && 'hidden', className)} {...props}>
        {children}
      </div>
    </TabGroupContext.Provider>
  );
};
